// Pure derivations for the /runs/<id> dashboard. All functions here are
// referentially transparent so component code stays declarative.

import type { RunDetail, RunSnapshot } from "@/api/types";

const RATE_WINDOW_SHORT_MIN = 5;
const RATE_WINDOW_LONG_MIN = 60;
const MS_PER_MIN = 60_000;

export interface RunBreakdown {
  // Counts that always exist.
  pushed: number;          // close_pushed: new lead created in Close
  duplicate: number;       // by_status.extracted - close_pushed: extracted, lead already in Close
  tagged: number;          // SKIPPED with non-empty tags: matched a check, no lead came out
  noMatch: number;         // SKIPPED with empty tags: no rule fired
  unreachable: number;     // by_status.unreachable: target server didn't respond (benign)
  errored: number;         // by_status.error: internal failure (extract crash, worker exception)
  pending: number;         // currently claimed and in-flight

  // Pre-pipeline (may be 0 if expected_total not known).
  preFiltered: number;     // run.skipped (normalize-rejects + already-owned + ignored)
  notYetRead: number;      // raw URLs producer hasn't read yet (only when expected_total known)

  // Roll-ups for the hero / tempo cards.
  extractedTotal: number;  // by_status.extracted (= pushed + duplicate)
  completedTerminal: number; // extracted + tagged + noMatch + unreachable + errored

  // Denominator for the "X / Y · Z%" headline. Excludes pre-filtered
  // URLs since those will never be processed. Null when we genuinely
  // don't have enough info to compute one (legacy runs without
  // expected_total and no work yet visible).
  effectiveTarget: number | null;
}

export function breakdown(run: RunDetail): RunBreakdown {
  const by = run.progress.by_status;
  const pushed = run.progress.close_pushed;
  const extractedTotal = by.extracted ?? 0;
  const duplicate = Math.max(0, extractedTotal - pushed);
  // Old runs finalised before `skipped_split` landed don't have real
  // split counts on their `final_progress` snapshot — the API still
  // returns the field but with both keys at 0. Detect that (split sums
  // to 0 while there are skipped rows) and lump everything into "no
  // match" so the math still adds up — better an honest over-count of
  // one bucket than missing data.
  const split = run.progress.skipped_split;
  const skippedTotal = by.skipped ?? 0;
  const splitSum = (split?.tagged ?? 0) + (split?.untagged ?? 0);
  const splitMissing = !split || (splitSum === 0 && skippedTotal > 0);
  const tagged = splitMissing ? 0 : (split?.tagged ?? 0);
  const noMatch = splitMissing ? skippedTotal : (split?.untagged ?? 0);
  const unreachable = by.unreachable ?? 0;
  const errored = by.error ?? 0;
  const pending = run.progress.pending;
  const preFiltered = run.skipped;

  const notYetRead =
    run.expected_total != null
      ? Math.max(0, run.expected_total - run.input_count)
      : 0;

  const completedTerminal = extractedTotal + tagged + noMatch + unreachable + errored;

  let effectiveTarget: number | null;
  if (run.expected_total != null) {
    effectiveTarget = Math.max(0, run.expected_total - preFiltered);
  } else {
    const rough = Math.max(run.total_targets, completedTerminal + pending);
    effectiveTarget = rough > 0 ? rough : null;
  }

  return {
    pushed,
    duplicate,
    tagged,
    noMatch,
    unreachable,
    errored,
    pending,
    preFiltered,
    notYetRead,
    extractedTotal,
    completedTerminal,
    effectiveTarget,
  };
}

// --- Rates -------------------------------------------------------------

type SnapshotScalar = (s: RunSnapshot) => number;

/**
 * Average rate per minute of `value(s)` over the past `windowMin`
 * minutes. Computed as `delta / windowMin` where the delta is the
 * `value` increase from the latest snapshot strictly before the
 * window (the baseline) up to the latest snapshot we have. When no
 * baseline exists — i.e. the run is younger than the window — the
 * pre-window value is treated as 0.
 *
 * Deliberately does NOT extrapolate from a partial sample. A run
 * that's been going 6 minutes with 10 pushes reads as
 * `10 / 60 = 0.167/min` (i.e. "10 in the last hour"), not
 * `10 / 6 = 1.67/min` slope-extrapolated to "100/hr." The displayed
 * `/hr` label then truthfully means "events in the last hour" and
 * naturally ramps up as the run accumulates more work.
 *
 * Returns null when there are no snapshots at all.
 */
export function ratePerMin(
  snapshots: RunSnapshot[],
  windowMin: number,
  value: SnapshotScalar,
  nowMs: number = Date.now(),
): number | null {
  if (snapshots.length === 0) return null;
  const cutoff = nowMs - windowMin * MS_PER_MIN;
  // Single walk: track the latest value strictly before the cutoff
  // (baseline) and the overall latest value. Snapshots arrive sorted
  // ascending by `at`.
  let baseline = 0;
  let baselineSeen = false;
  let last: RunSnapshot | null = null;
  for (const s of snapshots) {
    const ts = new Date(s.at).getTime();
    if (ts < cutoff) {
      baseline = value(s);
      baselineSeen = true;
    }
    last = s;
  }
  if (last == null) return null;
  const lastVal = value(last);
  // No baseline → run started inside the window; treat pre-window as 0.
  const delta = Math.max(0, lastVal - (baselineSeen ? baseline : 0));
  return delta / windowMin;
}

export interface TempoMetrics {
  extractedPerMin: number | null;
  extractedPerHour: number | null;
  pushedPerMin: number | null;
  pushedPerHour: number | null;
  processedPerMin: number | null;
}

// Sum of every domain status that means "done with this URL". Shared by
// the Processed/min tempo card and the ETA estimator so they agree on
// what "completed" means.
const terminalCount = (s: RunSnapshot): number =>
  (s.by_status.extracted ?? 0) +
  (s.by_status.skipped ?? 0) +
  (s.by_status.unreachable ?? 0) +
  (s.by_status.error ?? 0);

export function tempo(snapshots: RunSnapshot[]): TempoMetrics {
  return {
    extractedPerMin: ratePerMin(snapshots, RATE_WINDOW_SHORT_MIN, (s) => s.by_status.extracted ?? 0),
    extractedPerHour: hourFromMin(
      ratePerMin(snapshots, RATE_WINDOW_LONG_MIN, (s) => s.by_status.extracted ?? 0),
    ),
    pushedPerMin: ratePerMin(snapshots, RATE_WINDOW_SHORT_MIN, (s) => s.close_pushed),
    pushedPerHour: hourFromMin(
      ratePerMin(snapshots, RATE_WINDOW_LONG_MIN, (s) => s.close_pushed),
    ),
    processedPerMin: ratePerMin(
      snapshots,
      RATE_WINDOW_SHORT_MIN,
      terminalCount,
    ),
  };
}

function hourFromMin(perMin: number | null): number | null {
  return perMin == null ? null : perMin * 60;
}

// --- ETA ---------------------------------------------------------------

/**
 * Seconds until the last claimable URL is processed. Combines pending
 * (already in queue) with an estimate of the upstream pool that hasn't
 * been read yet, scaled by the observed pre-filter survival rate so we
 * don't over-promise on a source where most URLs get filtered.
 *
 * Returns null when we can't responsibly estimate (no rate yet,
 * effectively done).
 */
export function etaSeconds(
  run: RunDetail,
  snapshots: RunSnapshot[],
  br: RunBreakdown,
): number | null {
  const completedPerMin = ratePerMin(
    snapshots,
    RATE_WINDOW_SHORT_MIN,
    terminalCount,
  );
  if (!completedPerMin || completedPerMin <= 0) return null;

  const surviveRate = run.input_count > 0
    ? Math.max(0, 1 - run.skipped / run.input_count)
    : 1;
  const estimRemaining = br.pending + br.notYetRead * surviveRate;
  if (estimRemaining <= 0) return null;

  return (estimRemaining / completedPerMin) * 60;
}

// --- Formatting --------------------------------------------------------

export function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

export function formatRate(perMin: number | null): string {
  if (perMin == null) return "—";
  if (perMin < 1) return perMin.toFixed(2);
  if (perMin < 100) return perMin.toFixed(1);
  if (perMin < 1000) return Math.round(perMin).toLocaleString();
  return `${(perMin / 1000).toFixed(1)}k`;
}

export function elapsedSeconds(run: RunDetail, nowMs: number = Date.now()): number {
  const start = new Date(run.started_at).getTime();
  const end = run.finished_at ? new Date(run.finished_at).getTime() : nowMs;
  return Math.max(0, Math.floor((end - start) / 1000));
}
